import { saveAs } from 'file-saver'
import moment from 'moment-mini'
import { combineLatest, Observable, of, Subscription } from 'rxjs'
import {
  catchError,
  distinctUntilChanged,
  filter,
  first,
  map,
  skip,
  switchMap,
  take,
  timeout,
  withLatestFrom
} from 'rxjs/operators'
import { authSelectors } from 'src/app/core/auth/auth.selectors'
import { AppState } from 'src/app/core/core.state'
import { NotificationService } from 'src/app/core/notification.service'

import { Component, OnInit } from '@angular/core'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Store } from '@ngrx/store'
import { participationActions } from '../../booking/participation/participation.actions'
import { courseActions } from '../../course/course.actions'
import { productActions } from '../../product/product.actions'
import { productSelectors } from '../../product/product.selectors'
import { CollecStatusType, Payment, PayMethodType } from '../payment/payment'
import { paymentActions } from '../payment/payment.actions'
import { paymentSelectors } from '../payment/payment.selectors'
import { Sale, StatusCode } from '../sale'
import { SaleItem } from '../sale-item/sale-item'
import { saleItemActions } from '../sale-item/sale-item.actions'
import { saleItemSelectors } from '../sale-item/sale-item.selectors'
import { saleActions } from '../sale.actions'
import { saleSelectors } from '../sale.selectors'
import { Organization } from '../../organization/organization'
import { organizationSelectors } from '../../organization/organization.selectors'
import { Member } from '../../member/member'
import { environment } from 'src/environments/environment'
import {
  DocumentTemplateParams,
  DocumentTemplateType
} from '../../document-template/document-template'
import { DocumentTemplateService } from '../../document-template/document-template.service'
import { Dictionary } from '@ngrx/entity'
import { Product } from '../../product/product'
import { Actions } from '@ngrx/effects'
import { documentSelectors } from '../../document/document.selectors'
import { SaleService } from '../sale.service'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'
import { ActivatedRoute } from '@angular/router'
import { BreadCrumbActions } from 'src/app/shared/components/breadcrumb/breadcrumb.actions'
import { bookingActions } from '../../booking/booking.actions'
import { ConfigurationService } from '../../../shared/services/configuration.service'
import { bookingSelectors } from '../../booking/booking.selectors'
import { Booking } from '../../booking/booking'
import { courseSelectors } from '../../course/course.selectors'
import { Course } from '../../course/course'

@UntilDestroy()
@Component({
  selector: 'sale-show',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class SaleShowComponent implements OnInit {
  public saleBelongsCourseBookingsInCourse$: Observable<Booking[]>
  public showPaymentsSection: boolean = true
  public isLoading$: Observable<boolean>
  public selectedLanguage$: Observable<string>
  public saleId$: Observable<number>
  public sale$: Observable<Sale>
  public organization$: Observable<Organization>
  public saleItems$: Observable<SaleItem[]>
  public saleItems: SaleItem[]
  public saleItemsTotal$: Observable<string>
  public payments$: Observable<Payment[]>
  public actionState: string
  public subscriptions$: Subscription[] = []
  public bookingCancelable$: Observable<boolean>
  public member$: Observable<Member>
  public statusCode = StatusCode
  public invoiceMsg: string = ''
  public didFetchSaleId: number
  public headerText = 'saleModule.order'
  public firstCreditSale$: Observable<Sale>
  public showDownloadTicketsButton = environment.showDownloadTicketsButton
  public documentTicketTemplateIsLoading: boolean = false
  public documentTemplateIsLoading: boolean = false
  private productEntities: Dictionary<Product>
  public activeTicketingBlockModule =
    environment.moduleConfigs?.shopModule?.shopIndex?.ticketingBlock ?? false
  private courses$: Observable<Course[]>
  constructor(
    private store: Store<AppState>,
    private notificationService: NotificationService,
    private documentTemplateService: DocumentTemplateService,
    private actions$: Actions,
    public saleService: SaleService,
    private route: ActivatedRoute,
    private configurationService: ConfigurationService
  ) {}

  ngOnInit() {
    this.selectedLanguage$ = this.store.select(
      settingSelectors.selectById,
      'language'
    )

    this.isLoading$ = this.store.select(saleSelectors.selectIsLoading)
    this.member$ = this.store.select(authSelectors.selectMember)
    this.route.paramMap
      .pipe(
        map((params) => params.get('saleId')),
        filter((saleId): saleId is string => !!saleId),
        map((saleId) => Number(saleId)),
        filter((saleId): saleId is number => !isNaN(saleId)),
        withLatestFrom(this.member$)
      )
      .subscribe(([saleId, member]) => {
        console.log('Sale id route param', saleId)
        if (!saleId) {
          return
        }
        this.store.dispatch(
          BreadCrumbActions.addPageToHistory({
            pageName: 'saleModule.sale',
            url: `/sales/${saleId}`,
            showBackArrow: true,
            titleName: `sale id:${saleId}`
          })
        )
        if (!this.didFetchSaleId || this.didFetchSaleId != saleId) {
          this.didFetchSaleId = saleId
          this.actionState = null

          this.store.dispatch(
            saleActions.fetchOneById({
              id: saleId,
              params: RequestQueryBuilder.create({
                search: { customerPersonId: member.id },
                join: [
                  { field: 'organization' },
                  { field: 'creditSales' },
                  { field: 'documents' }
                  // { field: 'saleItems' }
                ]
              })
            })
          )

          this.store.dispatch(
            saleItemActions.fetchManyBySaleId({
              saleId: saleId,
              params: RequestQueryBuilder.create({
                join: [
                  { field: 'product' },
                  { field: 'product.linkedSubscriptions' },
                  { field: 'product.translations' },
                  { field: 'product.location' },
                  { field: 'product.documents' },
                  { field: 'subscription' },
                  { field: 'subscription.translations' },
                  { field: 'subscription.documents' },
                  { field: 'rental' }
                ]
              })
            })
          )

          this.store.dispatch(
            paymentActions.fetchMany({
              params: RequestQueryBuilder.create({
                search: { saleId: saleId, memberId: member.id }
              })
            })
          )
        }
      })
    this.saleId$ = this.route.paramMap.pipe(
      map((params) => params?.get('saleId')),
      filter((saleId): saleId is string => !!saleId),
      map((saleId) => Number(saleId)),
      filter((saleId): saleId is number => !isNaN(saleId))
    )

    this.sale$ = this.saleId$.pipe(
      switchMap((saleId) => this.store.select(saleSelectors.selectById, saleId))
    )

    this.store
      .select(productSelectors.selectEntities)
      .pipe(untilDestroyed(this))
      .subscribe((productEntities) => (this.productEntities = productEntities))

    this.organization$ = this.sale$.pipe(
      switchMap((sale) =>
        this.store.select(
          organizationSelectors.selectById,
          sale?.organizationId
        )
      )
    )

    this.firstCreditSale$ = combineLatest([
      this.saleId$,
      this.store.select(saleSelectors.selectAll)
    ]).pipe(
      map(([saleId, sales]) => sales.find((s) => s.isCreditOfSaleId == saleId))
    )

    this.payments$ = combineLatest([
      this.saleId$,
      this.store.select(paymentSelectors.selectAll)
    ]).pipe(
      map(([saleId, payments]) =>
        payments.filter((payment) => payment.saleId === saleId)
      )
    )

    this.saleItems$ = combineLatest([
      this.saleId$,
      this.store.select(saleItemSelectors.selectAll)
    ]).pipe(
      distinctUntilChanged((a, b) => JSON.stringify(a) == JSON.stringify(b)),
      map(([saleId, saleItems]) => {
        return saleItems
          .filter((saleItem) => saleItem.saleId == saleId)
          .sort((a, b) => a.bookingId - b.bookingId)
      })
    )
    this.saleBelongsCourseBookingsInCourse$ = this.saleItems$.pipe(
      switchMap((saleItems) => {
        if (
          saleItems.length > 0 &&
          saleItems.every(
            (s) => s.courseId != null && s.courseId === saleItems[0].courseId
          )
        ) {
          const courseId = saleItems[0].courseId
          return this.store.select(bookingSelectors.selectByFilter, {
            field: 'courseId',
            value: courseId
          })
        }
        return of([])
      })
    )
    this.courses$ = this.store.select(courseSelectors.selectAll)

    let didDispatchFetch = false
    this.saleItems$.pipe(untilDestroyed(this)).subscribe((saleItems) => {
      this.saleItems = saleItems
      if (saleItems?.length && !didDispatchFetch) {
        didDispatchFetch = true
        this.store.dispatch(
          productActions.fetchMany({
            params: RequestQueryBuilder.create({
              search: {
                id: {
                  [CondOperator.IN]: saleItems.map(
                    (saleItem) => saleItem.productId
                  )
                }
              }
            })
          })
        )
        this.store.dispatch(
          courseActions.fetchMany({
            params: RequestQueryBuilder.create({
              search: {
                id: {
                  [CondOperator.IN]: saleItems.map(
                    (saleItem) => saleItem?.courseId
                  )
                }
              },
              join: [
                { field: 'bookings' },
                { field: 'product' },
                { field: 'product.documents' },
                { field: 'product.linkedSubscriptions' }
              ]
            })
          })
        )
      }
    })
    combineLatest([
      this.store.select(authSelectors.selectMember),
      this.saleItems$,
      this.courses$
    ])
      .pipe(
        untilDestroyed(this),
        filter(
          ([member, saleItems]) => Boolean(member) && Boolean(saleItems.length)
        ),
        filter(([, saleItems, courses]) => {
          const courseIds = saleItems
            .map((si) => si.courseId)
            .filter((id) => Boolean(id))
          if (courseIds.length > 0) {
            const course = courses.find((c) => c.id === courseIds[0])
            return Boolean(course?.bookings?.length)
          }
          return true
        }),
        take(1)
      )
      .subscribe(([member, saleItems, courses]) => {
        const bookingIds = saleItems
          .map((saleItem) => saleItem.bookingId)
          .filter((id) => Boolean(id))
        const courseIds = saleItems
          .map((saleItem) => saleItem.courseId)
          .filter((id) => Boolean(id))
        const course =
          courseIds.length > 0
            ? courses.find((c) => c.id === courseIds[0])
            : null
        const courseBookingIds =
          course?.bookings?.map((b) => (typeof b === 'number' ? b : b.id)) ?? []

        const allBookingIds = [
          ...new Set([...bookingIds, ...courseBookingIds])
        ].filter((id) => Boolean(id))

        if (allBookingIds.length > 0) {
          this.store.dispatch(
            participationActions.fetchMany({
              params: RequestQueryBuilder.create({
                search: {
                  memberId: member.id,
                  bookingId: {
                    [CondOperator.IN]: allBookingIds
                  }
                }
              })
            })
          )
        }
      })
  }

  public onClickCancel() {
    this.sale$
      .pipe(
        skip(1),
        first(),
        timeout(5000),
        catchError(() => {
          return of(null)
        })
      )
      .subscribe((sale) => {
        if (sale.creditSales?.length > 0) {
          this.actionState = 'cancelled'
          this.notificationService.emit('saleModule.orderCanceled', 'info')
          this.store.dispatch(participationActions.resetState())
          this.store.dispatch(bookingActions.resetState())
          this.store.dispatch(
            saleActions.fetchMany({
              params: RequestQueryBuilder.create({
                search: {
                  customerPersonId: sale.customerPersonId,
                  isCreditOfSaleId: sale.id
                },
                join: [{ field: 'payments' }]
              })
            })
          )
        }
      })

    this.sale$.pipe(take(1)).subscribe((sale) =>
      this.store.dispatch(
        saleActions.updateOneById({
          id: sale.id,
          params: { statusCode: StatusCode.CANCELLED }
        })
      )
    )
  }
  public hasBalanceCreditedOnCancel(payments: Payment[]): boolean {
    return Boolean(
      payments.find((p) =>
        [
          PayMethodType.PAY_METHOD_CREDIT_CARD,
          PayMethodType.PAY_METHOD_ACCOUNT
        ].includes(p.payMethod)
      )
    )
  }

  hasSubscription(): boolean {
    return Boolean(this.saleItems.find((saleItem) => saleItem.subscriptionId))
  }

  public isCancellable(saleBelongsCourseBookingsInCourse: Booking[]) {
    if (!this.saleItems?.length) {
      return false
    }

    const bookingSaleItem = this.saleItems.find(
      (saleItem) =>
        saleItem.bookingId || saleItem.courseId || saleItem.subscriptionId
    )

    if (!bookingSaleItem) {
      const saleItemInPast = this.saleItems.find((saleItem) => {
        return (
          saleItem.params?.startDate &&
          moment(saleItem.params.startDate).isBefore(moment())
        )
      })

      if (saleItemInPast) {
        return false
      }

      let hasNonCancellableProduct = Boolean(
        this.saleItems.find(
          (saleItem) =>
            !this.productEntities[saleItem?.productId]?.bookable &&
            !this.productEntities[saleItem?.productId]?.allowAsLinkedProduct &&
            !saleItem.claimableEndDate
        )
      )

      if (hasNonCancellableProduct) {
        return false
      }

      return true
    }

    const bookingStartDate = saleBelongsCourseBookingsInCourse?.length
      ? [...saleBelongsCourseBookingsInCourse].sort(
          (a, b) =>
            moment(a.startDate).toDate().getTime() -
            moment(b.startDate).toDate().getTime()
        )[0]?.startDate
      : bookingSaleItem.params?.startDate

    if (moment().isAfter(moment(bookingStartDate))) {
      return false
    }

    let product = this.productEntities[bookingSaleItem.productId]

    const cancelableMinutesAhead = product?.bookableCancelableMinutesAhead
    if (
      cancelableMinutesAhead &&
      moment().isSameOrAfter(
        moment(bookingStartDate).subtract(cancelableMinutesAhead, 'minutes')
      )
    ) {
      return false
    }
    return true
  }

  public onClickDownloadTicket(saleId: number) {
    this.store
      .select(saleSelectors.selectById, saleId)
      .pipe(
        take(1),
        withLatestFrom(this.store.select(documentSelectors.selectEntities))
      )
      .subscribe(([sale, documentEntities]) => {
        if (sale.documents?.length) {
          console.log(
            'onClickDownloadTicket got preloaded documents and will use these',
            sale.documents
          )
          for (const documentId of sale.documents) {
            if (documentEntities[documentId]?.publicUrl?.length) {
              window
                .open(documentEntities[documentId]?.publicUrl, '_blank')
                .focus()
            }
          }
        } else {
          this.documentTicketTemplateIsLoading = true

          const params: DocumentTemplateParams = {
            documentTemplateType: DocumentTemplateType.TICKET,
            saleId
          }

          this.documentTemplateService
            .getOneByParams(params)
            .pipe(take(1))
            .subscribe(
              (response) => {
                const blob = new Blob([response], { type: 'application/pdf' })
                saveAs(blob, `sale-${saleId}.pdf`)

                this.documentTicketTemplateIsLoading = false
              },
              (error) => {
                this.documentTicketTemplateIsLoading = false
                console.error('Error downloading the PDF', error)
              }
            )
        }
      })
  }

  public onClickDownload(saleId: number) {
    this.documentTemplateIsLoading = true

    const params: DocumentTemplateParams = {
      documentTemplateType: DocumentTemplateType.SALE_EXTRACT,
      saleId
    }

    combineLatest([
      this.documentTemplateService.getOneByParams(params),
      this.configurationService.isMobileApp$
    ])
      .pipe(take(1))
      .subscribe(
        async ([response, isMobileApp]) => {
          const blob = new Blob([response], { type: 'application/pdf' })
          const documentName = `sale-${saleId}.pdf`
          if (isMobileApp) {
            const reader = new FileReader()
            reader.onloadend = () => {
              const base64data = reader.result
              this.configurationService.sendEventToAPP({
                eventType: 'DOWNLOAD_BASE64',
                payload: {
                  filename: documentName,
                  base64: base64data as string
                }
              })
            }
            reader.readAsDataURL(blob)
          } else {
            saveAs(blob, documentName)
          }

          this.documentTemplateIsLoading = false
        },
        (error) => {
          this.documentTemplateIsLoading = false
          console.error('Error downloading the PDF', error)
        }
      )
  }
  public saleHasCreditSale(sale: Sale): boolean {
    return sale?.creditSales?.length == 0 ? false : true
  }
}
